import {DateTime} from "luxon";
import type {EBirdChecklistByFamily, EBirdMyDataSchema} from "ebird-mydata-reader";
import {getMacaulayCatalogNumbers} from "../../../helpers/macaulayLibrary";
import {buildHotspotHabitatProfile} from "../../../helpers/habitat-trust";
import type {EnrichedObservationsByLocation} from "../../../helpers/types";
import {getUniqueSpeciesCount, getYear} from "../helpers";
import {buildHotspotDetailPath, getCountryLabel, getStateProvinceLabel} from "../../hotspot-explorer/helpers";
import {getFamilySlug} from "../../../helpers/helpers";
import {
    ACHIEVEMENT_SET_DEFINITIONS,
    ACHIEVEMENT_SEASON_LABELS,
    ACHIEVEMENT_THEME_LABELS,
    ACHIEVEMENT_THEME_ROUTES,
    ACHIEVEMENTS_ROUTE
} from "./constants";
import type {
    AchievementDashboard,
    AchievementDataPoint,
    AchievementMetricSnapshot,
    AchievementMilestoneProgress,
    AchievementSetDetail,
    AchievementSetDefinition,
    AchievementUnlockContext,
    AchievementMetricId,
    AchievementMilestoneDefinition,
    AchievementSetSummary,
    AchievementTheme,
    AchievementsViewProps,
    EarnedAchievement,
    InProgressAchievement
} from "./types";

export const buildAchievementThemePath = (theme: AchievementTheme | 'all') => {
    return theme === 'all' ? ACHIEVEMENTS_ROUTE : ACHIEVEMENT_THEME_ROUTES[theme];
}

export const getAchievementThemeFromPath = (pathname: string): AchievementTheme | 'all' | undefined => {
    const normalizedPath = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
    if (normalizedPath === ACHIEVEMENTS_ROUTE) return 'all';

    return (Object.entries(ACHIEVEMENT_THEME_ROUTES) as Array<[AchievementTheme, string]>)
        .find(([, route]) => route === normalizedPath)?.[0];
}

const ACHIEVEMENT_SET_DEFINITION_BY_ID = new Map<string, AchievementSetDefinition>(
    ACHIEVEMENT_SET_DEFINITIONS.map(setDefinition => [setDefinition.id, setDefinition])
);

type AchievementDerivedDataCache = {
    rawData: AchievementsViewProps['rawData'];
    lifeList: AchievementsViewProps['lifeList'];
    observationsByLocation: AchievementsViewProps['observationsByLocation'];
    checklist: AchievementsViewProps['checklist'];
    enrichmentSignature: string;
    metrics: AchievementMetricSnapshot;
    sets: AchievementSetSummary[];
    milestoneDates: Map<string, MilestoneUnlockDetail>;
    earnedAchievements: EarnedAchievement[];
    inProgress: InProgressAchievement[];
};

let achievementDerivedDataCache: AchievementDerivedDataCache | undefined;

export const buildAchievementEnrichmentSignature = (
    observationsByLocation: AchievementsViewProps['observationsByLocation']
) => {
    let hash = 2166136261;
    let enrichedLocationCount = 0;

    const addValue = (value: unknown) => {
        const text = String(value ?? '');
        for (let index = 0; index < text.length; index += 1) {
            hash ^= text.charCodeAt(index);
            hash = Math.imul(hash, 16777619);
        }
    };

    observationsByLocation.forEach(locationGroup => {
        const enrichedLocation = locationGroup as EnrichedObservationsByLocation;
        const {ecoData, habitatOverride, habitatFeedbackSummary} = enrichedLocation;
        if (ecoData || habitatOverride || habitatFeedbackSummary) enrichedLocationCount += 1;

        addValue(locationGroup.locationId || locationGroup.location);
        addValue(ecoData?.matched);
        addValue(ecoData?.biomeName);
        addValue(ecoData?.ecoregionName);
        addValue(ecoData?.habitat_local_primary);
        addValue(ecoData?.habitat_primary);
        addValue(ecoData?.habitat_group);
        addValue(habitatOverride?.overrideDisplayHabitat);
        addValue(habitatOverride?.overrideHabitat);
        addValue(habitatFeedbackSummary?.userConfirmedMatch);
    });

    return `${observationsByLocation.length}:${enrichedLocationCount}:${hash >>> 0}`;
}

const getDateTime = (date?: string, time?: string) => {
    return DateTime.fromISO(`${date ?? ''}T${time || '00:00'}`).toMillis() || 0;
}

const getCountryCode = (observation: EBirdMyDataSchema) => {
    return observation.stateProvince?.split('-')[0] || '';
}

const getCountyKey = (observation: EBirdMyDataSchema) => {
    if (!observation.stateProvince || !observation.county) return '';

    return `${observation.stateProvince}|${observation.county}`;
}

const getSeenFamilyCount = (checklist: EBirdChecklistByFamily[]) => {
    return checklist.filter(family => family.seen && family.speciesList.some(species => !species.isExtinct)).length;
}

const getAchievementHabitatLabel = (locationGroup: EnrichedObservationsByLocation) => {
    const habitatProfile = buildHotspotHabitatProfile(locationGroup);
    if (!habitatProfile || habitatProfile.displayHabitat.startsWith('Possibly ')) {
        return '';
    }

    return habitatProfile.displayHabitat.trim();
}

const getAchievementBiomeLabel = (locationGroup: EnrichedObservationsByLocation) => {
    return locationGroup.ecoData?.matched ? locationGroup.ecoData.biomeName?.trim() ?? '' : '';
}

const getAchievementEcoregionLabel = (locationGroup: EnrichedObservationsByLocation) => {
    return locationGroup.ecoData?.matched ? locationGroup.ecoData.ecoregionName?.trim() ?? '' : '';
}

type LocationClassificationLabelGetter = (locationGroup: EnrichedObservationsByLocation) => string;

const getUniqueLocationClassificationCount = (
    observationsByLocation: AchievementsViewProps['observationsByLocation'],
    getLabel: LocationClassificationLabelGetter
) => {
    return new Set(
        observationsByLocation
            .map(locationGroup => getLabel(locationGroup as EnrichedObservationsByLocation))
            .filter(Boolean)
    ).size;
}

const isExcludedBigYearTaxon = (observation: EBirdMyDataSchema) => {
    const taxonLabel = `${observation.commonName ?? ''} ${observation.scientificName ?? ''}`;

    return /\bsp\./i.test(taxonLabel) ||
        /\bhybrid\b/i.test(taxonLabel) ||
        /\s+x\s+/i.test(taxonLabel);
}

const getSpeciesDetailPathForObservation = (
    observation: EBirdMyDataSchema,
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    return observation.taxonomicOrder
        ? speciesDetailPathByTaxonomicOrder.get(observation.taxonomicOrder)
        : undefined;
}

const isBigYearSpeciesObservation = (
    observation: EBirdMyDataSchema,
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    return Boolean(getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)) &&
        !isExcludedBigYearTaxon(observation);
}

const getBigYearSpeciesCount = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const speciesByYear = new Map<string, Set<string>>();

    rawData.forEach(observation => {
        const year = getYear(observation.date);
        const speciesKey = getObservationSpeciesKey(observation);
        if (!year || !speciesKey || !isBigYearSpeciesObservation(observation, speciesDetailPathByTaxonomicOrder)) return;

        const seenSpeciesForYear = speciesByYear.get(year) ?? new Set<string>();
        seenSpeciesForYear.add(speciesKey);
        speciesByYear.set(year, seenSpeciesForYear);
    });

    return Math.max(0, ...Array.from(speciesByYear.values()).map(species => species.size));
}

const getBigDaySpeciesCount = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const speciesByDate = new Map<string, Set<string>>();

    rawData.forEach(observation => {
        const date = observation.date;
        const speciesKey = getObservationSpeciesKey(observation);
        if (!date || !speciesKey || !isBigYearSpeciesObservation(observation, speciesDetailPathByTaxonomicOrder)) return;

        const seenSpeciesForDate = speciesByDate.get(date) ?? new Set<string>();
        seenSpeciesForDate.add(speciesKey);
        speciesByDate.set(date, seenSpeciesForDate);
    });

    return Math.max(0, ...Array.from(speciesByDate.values()).map(species => species.size));
}

const getBigChecklistSpeciesCount = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const speciesByChecklist = new Map<string, Set<string>>();

    rawData.forEach(observation => {
        const checklistKey = getChecklistKey(observation);
        const speciesKey = getObservationSpeciesKey(observation);
        if (!checklistKey || !speciesKey || !isBigYearSpeciesObservation(observation, speciesDetailPathByTaxonomicOrder)) return;

        const species = speciesByChecklist.get(checklistKey) ?? new Set<string>();
        species.add(speciesKey);
        speciesByChecklist.set(checklistKey, species);
    });

    return Math.max(0, ...Array.from(speciesByChecklist.values()).map(species => species.size));
}

const getCurrentYearSpeciesCount = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const currentYear = DateTime.now().year.toString();
    return new Set(
        rawData
            .filter(observation => getYear(observation.date) === currentYear)
            .filter(observation => isBigYearSpeciesObservation(observation, speciesDetailPathByTaxonomicOrder))
            .map(getObservationSpeciesKey)
            .filter(Boolean)
    ).size;
}

const getObservationSpeciesKey = (observation: EBirdMyDataSchema) => {
    return observation.taxonomicOrder?.toString() || observation.commonName || observation.scientificName || '';
}

const getObservationGenus = (observation: EBirdMyDataSchema) => {
    return observation.scientificName?.trim().split(/\s+/)[0] ?? '';
}

const getChecklistKey = (observation: EBirdMyDataSchema) => {
    return observation.submissionId || `${observation.date ?? ''}|${observation.locationId || observation.location || ''}`;
}

const getPhotoSpeciesCount = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    return new Set(
        rawData
            .filter(observation => getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length > 0)
            .filter(observation => Boolean(getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)))
            .map(getObservationSpeciesKey)
            .filter(Boolean)
    ).size;
}

const getPhotoCoverage = (
    rawData: EBirdMyDataSchema[],
    lifeList: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const lifeListSpecies = new Set(lifeList.map(getObservationSpeciesKey).filter(Boolean));
    if (!lifeListSpecies.size) return 0;

    const photographedLifeListSpecies = new Set(
        rawData
            .filter(observation => getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length > 0)
            .filter(observation => Boolean(getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)))
            .map(getObservationSpeciesKey)
            .filter(speciesKey => lifeListSpecies.has(speciesKey))
    );

    return Math.round((photographedLifeListSpecies.size / lifeListSpecies.size) * 100);
}

const getPhotoFamilyCount = (rawData: EBirdMyDataSchema[], checklist: EBirdChecklistByFamily[]) => {
    const familyBySpeciesKey = getFamilyBySpeciesKey(checklist);

    return new Set(
        rawData
            .filter(observation => getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length > 0)
            .map(observation => familyBySpeciesKey.get(getObservationSpeciesKey(observation))?.familyCommon)
            .filter((familyName): familyName is string => Boolean(familyName))
    ).size;
}

const getIllustratedChecklistCount = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const photographedSpeciesByChecklist = new Map<string, Set<string>>();

    rawData.forEach(observation => {
        const checklistKey = getChecklistKey(observation);
        const speciesKey = getObservationSpeciesKey(observation);
        if (
            !checklistKey ||
            !speciesKey ||
            getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length <= 0 ||
            !getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)
        ) return;

        const photographedSpecies = photographedSpeciesByChecklist.get(checklistKey) ?? new Set<string>();
        photographedSpecies.add(speciesKey);
        photographedSpeciesByChecklist.set(checklistKey, photographedSpecies);
    });

    return Math.max(0, ...Array.from(photographedSpeciesByChecklist.values()).map(species => species.size));
}

const getLiferLensCount = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const firstDateBySpecies = new Map<string, string>();
    const photographedOnFirstDate = new Set<string>();

    getSortedObservations(rawData).forEach(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        if (!speciesKey || !observation.date || !getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)) return;

        const firstDate = firstDateBySpecies.get(speciesKey);
        if (!firstDate) {
            firstDateBySpecies.set(speciesKey, observation.date);
        }

        if (
            (firstDate ?? observation.date) === observation.date &&
            getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length > 0
        ) {
            photographedOnFirstDate.add(speciesKey);
        }
    });

    return photographedOnFirstDate.size;
}

const getGenusCount = (rawData: EBirdMyDataSchema[], speciesDetailPathByTaxonomicOrder: Map<number, string>) => {
    return new Set(
        rawData
            .filter(observation => Boolean(getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)))
            .map(getObservationGenus)
            .filter(Boolean)
    ).size;
}

const getYearRoundSpeciesCount = (rawData: EBirdMyDataSchema[], speciesDetailPathByTaxonomicOrder: Map<number, string>) => {
    const quartersBySpecies = new Map<string, Set<number>>();

    rawData.forEach(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        const quarter = DateTime.fromISO(observation.date ?? '').quarter;
        if (!speciesKey || !quarter || !getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)) return;

        const quarters = quartersBySpecies.get(speciesKey) ?? new Set<number>();
        quarters.add(quarter);
        quartersBySpecies.set(speciesKey, quarters);
    });

    return Array.from(quartersBySpecies.values()).filter(quarters => quarters.size === 4).length;
}

const getLiferRushCount = (rawData: EBirdMyDataSchema[], speciesDetailPathByTaxonomicOrder: Map<number, string>) => {
    const lifersByDate = new Map<string, Set<string>>();
    const seenSpecies = new Set<string>();

    getSortedObservations(rawData).forEach(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        if (!observation.date || !speciesKey || seenSpecies.has(speciesKey) || !getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)) return;

        seenSpecies.add(speciesKey);
        const lifers = lifersByDate.get(observation.date) ?? new Set<string>();
        lifers.add(speciesKey);
        lifersByDate.set(observation.date, lifers);
    });

    return Math.max(0, ...Array.from(lifersByDate.values()).map(lifers => lifers.size));
}

const getChecklistFamilyCount = (rawData: EBirdMyDataSchema[], checklist: EBirdChecklistByFamily[]) => {
    const familyBySpeciesKey = getFamilyBySpeciesKey(checklist);
    const familiesByChecklist = new Map<string, Set<string>>();

    rawData.forEach(observation => {
        const family = familyBySpeciesKey.get(getObservationSpeciesKey(observation));
        const checklistKey = getChecklistKey(observation);
        if (!family || !checklistKey) return;

        const families = familiesByChecklist.get(checklistKey) ?? new Set<string>();
        families.add(family.familyCommon);
        familiesByChecklist.set(checklistKey, families);
    });

    return Math.max(0, ...Array.from(familiesByChecklist.values()).map(families => families.size));
}

const getFamiliarRegularCount = (
    rawData: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>,
    requiredDates: number
) => {
    const datesBySpecies = new Map<string, Set<string>>();

    rawData.forEach(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        if (!speciesKey || !observation.date || !getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)) return;

        const dates = datesBySpecies.get(speciesKey) ?? new Set<string>();
        dates.add(observation.date);
        datesBySpecies.set(speciesKey, dates);
    });

    return Array.from(datesBySpecies.values()).filter(dates => dates.size >= requiredDates).length;
}

const getSpeciesDetailPathByTaxonomicOrder = (checklist: EBirdChecklistByFamily[]) => {
    const speciesDetailPathByTaxonomicOrder = new Map<number, string>();

    for (const family of checklist) {
        const familySlug = getFamilySlug(family.familyName);

        for (const species of family.speciesList) {
            const detailPath = `/family-explorer/family/${familySlug}/species/${species.speciesCode}`;
            speciesDetailPathByTaxonomicOrder.set(species.taxonomicOrder, detailPath);

            for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                speciesDetailPathByTaxonomicOrder.set(alternateTaxonomicOrder, detailPath);
            }
        }
    }

    return speciesDetailPathByTaxonomicOrder;
}

const getLocationGroupKey = (locationGroup: AchievementsViewProps['observationsByLocation'][number]) => {
    return locationGroup.locationId || locationGroup.location || '';
}

const getSortedObservations = (rawData: EBirdMyDataSchema[]) => {
    return [...rawData].sort((left, right) => getDateTime(left.date, left.time) - getDateTime(right.date, right.time));
}

const getBirdingStreakProgressEvents = (rawData: EBirdMyDataSchema[]) => {
    const firstObservationByDate = new Map<string, EBirdMyDataSchema>();

    getSortedObservations(rawData).forEach(observation => {
        const date = DateTime.fromISO(observation.date ?? '').startOf('day');
        const dateKey = date.isValid ? date.toISODate() : null;
        if (!dateKey || firstObservationByDate.has(dateKey)) return;

        firstObservationByDate.set(dateKey, observation);
    });

    let previousDate: DateTime | undefined;
    let streak = 0;

    return Array.from(firstObservationByDate.entries()).map(([dateKey, observation]) => {
        const date = DateTime.fromISO(dateKey).startOf('day');
        streak = previousDate && date.diff(previousDate, 'days').days === 1 ? streak + 1 : 1;
        previousDate = date;

        return {
            value: streak,
            date: dateKey,
            observation
        };
    });
}

const getLongestBirdingStreak = (rawData: EBirdMyDataSchema[]) => {
    return Math.max(0, ...getBirdingStreakProgressEvents(rawData).map(event => event.value));
}

const getWeekendIdentity = (dateValue?: string) => {
    const date = DateTime.fromISO(dateValue ?? '').startOf('day');
    if (!date.isValid || (date.weekday !== 6 && date.weekday !== 7)) return undefined;

    return {
        year: date.year.toString(),
        weekendKey: (date.weekday === 7 ? date.minus({days: 1}) : date).toISODate() ?? ''
    };
}

const getWeekendWarriorProgressEvents = (rawData: EBirdMyDataSchema[]) => {
    const weekendsByYear = new Map<string, Set<string>>();

    return getSortedObservations(rawData).flatMap(observation => {
        const weekend = getWeekendIdentity(observation.date);
        if (!weekend) return [];

        const yearWeekends = weekendsByYear.get(weekend.year) ?? new Set<string>();
        if (yearWeekends.has(weekend.weekendKey)) return [];

        yearWeekends.add(weekend.weekendKey);
        weekendsByYear.set(weekend.year, yearWeekends);

        return [{
            value: Math.max(...Array.from(weekendsByYear.values()).map(weekends => weekends.size)),
            year: weekend.year,
            date: observation.date,
            observation
        }];
    });
}

const getWeekendWarriorCount = (rawData: EBirdMyDataSchema[]) => {
    return Math.max(0, ...getWeekendWarriorProgressEvents(rawData).map(event => event.value));
}

const getLocationClassificationProgressEvents = (
    sortedObservations: EBirdMyDataSchema[],
    observationsByLocation: AchievementsViewProps['observationsByLocation'],
    getLabel: LocationClassificationLabelGetter
) => {
    const earliestObservationByLocation = new Map<string, {date?: string, sortTime: number}>();

    for (const observation of sortedObservations) {
        const locationKey = observation.locationId || observation.location || '';
        if (!locationKey || earliestObservationByLocation.has(locationKey)) continue;

        earliestObservationByLocation.set(locationKey, {
            date: observation.date,
            sortTime: getDateTime(observation.date, observation.time)
        });
    }

    const classificationEvents = observationsByLocation
        .flatMap(locationGroup => {
            const firstObservation = earliestObservationByLocation.get(getLocationGroupKey(locationGroup));
            const label = getLabel(locationGroup as EnrichedObservationsByLocation);
            if (!label || !firstObservation) return [];

            return [{
                label,
                date: firstObservation.date,
                sortTime: firstObservation.sortTime,
                location: locationGroup.location,
                locationId: locationGroup.locationId || ''
            }];
        })
        .sort((left, right) => left.sortTime - right.sortTime);
    const seenLabels = new Set<string>();

    return classificationEvents.flatMap(event => {
        if (seenLabels.has(event.label)) return [];

        seenLabels.add(event.label);
        return [{
            value: seenLabels.size,
            date: event.date,
            unlockContext: {
                primaryLabel: event.label,
                secondaryLabel: event.location,
                secondaryDetailPath: event.locationId ? buildHotspotDetailPath(event.locationId) : undefined
            }
        }];
    });
}

type MilestoneUnlockDetail = {
    date?: string,
    unlockContext?: AchievementUnlockContext
}

const assignMilestoneDatesFromProgress = (
    milestoneDates: Map<string, MilestoneUnlockDetail>,
    milestones: AchievementMilestoneDefinition[],
    progressEvents: Array<{value: number, date?: string, unlockContext?: AchievementUnlockContext}>
) => {
    const sortedMilestones = [...milestones].sort((left, right) => left.threshold - right.threshold);
    let milestoneIndex = 0;

    for (const event of progressEvents) {
        while (milestoneIndex < sortedMilestones.length && event.value >= sortedMilestones[milestoneIndex].threshold) {
            milestoneDates.set(sortedMilestones[milestoneIndex].id, {
                date: event.date,
                unlockContext: event.unlockContext
            });
            milestoneIndex += 1;
        }

        if (milestoneIndex >= sortedMilestones.length) {
            break;
        }
    }
}

const getFamilyBySpeciesKey = (checklist: EBirdChecklistByFamily[]) => {
    const familyBySpeciesKey = new Map<string, {familyCommon: string, familySlug: string}>();

    for (const family of checklist) {
        const familySlug = getFamilySlug(family.familyName);
        for (const species of family.speciesList) {
            familyBySpeciesKey.set(species.taxonomicOrder.toString(), {
                familyCommon: family.familyCommon,
                familySlug
            });

            for (const alternateTaxonomicOrder of species.alternateTaxonomicOrders ?? []) {
                familyBySpeciesKey.set(alternateTaxonomicOrder.toString(), {
                    familyCommon: family.familyCommon,
                    familySlug
                });
            }
        }
    }

    return familyBySpeciesKey;
}

const getFamilyProgressEvents = (sortedObservations: EBirdMyDataSchema[], checklist: EBirdChecklistByFamily[]) => {
    const familyBySpeciesKey = getFamilyBySpeciesKey(checklist);
    const seenFamilies = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const family = familyBySpeciesKey.get(getObservationSpeciesKey(observation));
        if (!family || seenFamilies.has(family.familyCommon)) return [];

        seenFamilies.add(family.familyCommon);
        return [{
            value: seenFamilies.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: family.familyCommon,
                primaryDetailPath: `/family-explorer/family/${family.familySlug}`,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getPhotoFamilyProgressEvents = (sortedObservations: EBirdMyDataSchema[], checklist: EBirdChecklistByFamily[]) => {
    const familyBySpeciesKey = getFamilyBySpeciesKey(checklist);
    const photographedFamilies = new Set<string>();

    return sortedObservations.flatMap(observation => {
        if (getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length <= 0) return [];

        const family = familyBySpeciesKey.get(getObservationSpeciesKey(observation));
        if (!family || photographedFamilies.has(family.familyCommon)) return [];

        photographedFamilies.add(family.familyCommon);
        return [{
            value: photographedFamilies.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: family.familyCommon,
                primaryDetailPath: `/family-explorer/family/${family.familySlug}`,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getPhotoSpeciesProgressEvents = (
    sortedObservations: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const photographedSpecies = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
        if (
            getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length <= 0 ||
            !speciesKey ||
            !speciesDetailPath ||
            photographedSpecies.has(speciesKey)
        ) return [];

        photographedSpecies.add(speciesKey);
        return [{
            value: photographedSpecies.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: observation.commonName || 'Unknown species',
                primaryDetailPath: speciesDetailPath,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getIllustratedChecklistProgressEvents = (
    sortedObservations: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const photographedSpeciesByChecklist = new Map<string, Set<string>>();

    return sortedObservations.flatMap(observation => {
        const checklistKey = getChecklistKey(observation);
        const speciesKey = getObservationSpeciesKey(observation);
        const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
        if (
            !checklistKey ||
            !speciesKey ||
            !speciesDetailPath ||
            getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length <= 0
        ) return [];

        const photographedSpecies = photographedSpeciesByChecklist.get(checklistKey) ?? new Set<string>();
        const previousCount = photographedSpecies.size;
        photographedSpecies.add(speciesKey);
        photographedSpeciesByChecklist.set(checklistKey, photographedSpecies);
        if (photographedSpecies.size === previousCount) return [];

        return [{
            value: photographedSpecies.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: observation.commonName || 'Unknown species',
                primaryDetailPath: speciesDetailPath,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getLiferLensProgressEvents = (
    sortedObservations: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const firstDateBySpecies = new Map<string, string>();
    const photographedLifers = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
        if (!speciesKey || !observation.date || !speciesDetailPath) return [];

        const firstDate = firstDateBySpecies.get(speciesKey);
        if (!firstDate) {
            firstDateBySpecies.set(speciesKey, observation.date);
        }
        if (
            (firstDate ?? observation.date) !== observation.date ||
            photographedLifers.has(speciesKey) ||
            getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length <= 0
        ) return [];

        photographedLifers.add(speciesKey);
        return [{
            value: photographedLifers.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: observation.commonName || 'Unknown species',
                primaryDetailPath: speciesDetailPath,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getGenusProgressEvents = (sortedObservations: EBirdMyDataSchema[], speciesDetailPathByTaxonomicOrder: Map<number, string>) => {
    const seenGenera = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const genus = getObservationGenus(observation);
        if (!genus || seenGenera.has(genus) || !getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder)) return [];

        seenGenera.add(genus);
        return [{
            value: seenGenera.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: genus,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getYearRoundProgressEvents = (sortedObservations: EBirdMyDataSchema[], speciesDetailPathByTaxonomicOrder: Map<number, string>) => {
    const quartersBySpecies = new Map<string, Set<number>>();
    const yearRoundSpecies = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        const quarter = DateTime.fromISO(observation.date ?? '').quarter;
        const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
        if (!speciesKey || !quarter || !speciesDetailPath || yearRoundSpecies.has(speciesKey)) return [];

        const quarters = quartersBySpecies.get(speciesKey) ?? new Set<number>();
        quarters.add(quarter);
        quartersBySpecies.set(speciesKey, quarters);
        if (quarters.size < 4) return [];

        yearRoundSpecies.add(speciesKey);
        return [{
            value: yearRoundSpecies.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: observation.commonName || 'Unknown species',
                primaryDetailPath: speciesDetailPath,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getLiferRushProgressEvents = (sortedObservations: EBirdMyDataSchema[], speciesDetailPathByTaxonomicOrder: Map<number, string>) => {
    const seenSpecies = new Set<string>();
    const lifersByDate = new Map<string, Set<string>>();

    return sortedObservations.flatMap(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
        if (!observation.date || !speciesKey || !speciesDetailPath || seenSpecies.has(speciesKey)) return [];

        seenSpecies.add(speciesKey);
        const lifers = lifersByDate.get(observation.date) ?? new Set<string>();
        lifers.add(speciesKey);
        lifersByDate.set(observation.date, lifers);
        return [{
            value: lifers.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: observation.commonName || 'Unknown species',
                primaryDetailPath: speciesDetailPath,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getChecklistFamilyProgressEvents = (sortedObservations: EBirdMyDataSchema[], checklist: EBirdChecklistByFamily[]) => {
    const familyBySpeciesKey = getFamilyBySpeciesKey(checklist);
    const familiesByChecklist = new Map<string, Set<string>>();

    return sortedObservations.flatMap(observation => {
        const family = familyBySpeciesKey.get(getObservationSpeciesKey(observation));
        const checklistKey = getChecklistKey(observation);
        if (!family || !checklistKey) return [];

        const families = familiesByChecklist.get(checklistKey) ?? new Set<string>();
        const priorCount = families.size;
        families.add(family.familyCommon);
        familiesByChecklist.set(checklistKey, families);
        if (families.size === priorCount) return [];

        return [{
            value: families.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: family.familyCommon,
                primaryDetailPath: `/family-explorer/family/${family.familySlug}`,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getFamiliarFacesProgressEvents = (
    sortedObservations: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>,
    requiredDates: number
) => {
    const datesBySpecies = new Map<string, Set<string>>();
    const regularSpecies = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
        if (!speciesKey || !observation.date || !speciesDetailPath || regularSpecies.has(speciesKey)) return [];

        const dates = datesBySpecies.get(speciesKey) ?? new Set<string>();
        dates.add(observation.date);
        datesBySpecies.set(speciesKey, dates);
        if (dates.size < requiredDates) return [];

        regularSpecies.add(speciesKey);
        return [{
            value: regularSpecies.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: observation.commonName || 'Unknown species',
                primaryDetailPath: speciesDetailPath,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getCountryProgressEvents = (sortedObservations: EBirdMyDataSchema[]) => {
    const seenCountries = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const countryCode = getCountryCode(observation);
        if (!countryCode || seenCountries.has(countryCode)) return [];

        seenCountries.add(countryCode);
        return [{
            value: seenCountries.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: getCountryLabel(countryCode),
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const buildYearRoundDataPointCollection = (
    sortedObservations: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
): AchievementDataPoint[] => {
    const sightingsBySpecies = new Map<string, {
        label: string,
        detailPath: string,
        sightingsByQuarter: Map<number, NonNullable<AchievementDataPoint['seasonSightings']>[number]>
    }>();
    const dataPoints: AchievementDataPoint[] = [];

    for (const observation of sortedObservations) {
        const speciesKey = getObservationSpeciesKey(observation);
        const quarter = DateTime.fromISO(observation.date ?? '').quarter;
        const detailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
        if (!speciesKey || !quarter || !detailPath) continue;

        const species = sightingsBySpecies.get(speciesKey) ?? {
            label: observation.commonName || 'Unknown species',
            detailPath,
            sightingsByQuarter: new Map()
        };
        if (species.sightingsByQuarter.has(quarter)) continue;

        species.sightingsByQuarter.set(quarter, {
            season: ACHIEVEMENT_SEASON_LABELS[quarter],
            firstObservedDate: observation.date,
            locationLabel: observation.location,
            locationDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
        });
        sightingsBySpecies.set(speciesKey, species);

        if (species.sightingsByQuarter.size === 4) {
            dataPoints.push({
                label: species.label,
                detailPath: species.detailPath,
                seasonSightings: [1, 2, 3, 4].map(quarterKey => species.sightingsByQuarter.get(quarterKey)!)
            });
        }
    }

    return dataPoints;
}

const getCountyProgressEvents = (sortedObservations: EBirdMyDataSchema[]) => {
    const seenCounties = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const countyKey = getCountyKey(observation);
        if (!countyKey || seenCounties.has(countyKey)) return [];

        seenCounties.add(countyKey);
        return [{
            value: seenCounties.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: observation.county || 'Unknown county',
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getStateProvinceProgressEvents = (sortedObservations: EBirdMyDataSchema[]) => {
    const seenStateProvinces = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const stateProvince = observation.stateProvince;
        if (!stateProvince || seenStateProvinces.has(stateProvince)) return [];

        seenStateProvinces.add(stateProvince);
        return [{
            value: seenStateProvinces.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: getStateProvinceLabel(stateProvince),
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const getSpeciesProgressEvents = (
    sortedObservations: EBirdMyDataSchema[],
    speciesDetailPathByTaxonomicOrder: Map<number, string>
) => {
    const seenSpecies = new Set<string>();

    return sortedObservations.flatMap(observation => {
        const speciesKey = getObservationSpeciesKey(observation);
        const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
        if (!speciesKey || !speciesDetailPath || seenSpecies.has(speciesKey)) return [];

        seenSpecies.add(speciesKey);
        return [{
            value: seenSpecies.size,
            date: observation.date,
            unlockContext: {
                primaryLabel: observation.commonName || 'Unknown species',
                primaryDetailPath: speciesDetailPath,
                secondaryLabel: observation.location,
                secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
            }
        }];
    });
}

const buildMilestoneUnlockDates = ({rawData, lifeList, observationsByLocation, checklist}: AchievementsViewProps) => {
    const milestoneDates = new Map<string, MilestoneUnlockDetail>();
    const sortedObservations = getSortedObservations(rawData);
    const setById = new Map(ACHIEVEMENT_SET_DEFINITIONS.map(setDefinition => [setDefinition.id, setDefinition]));
    const speciesDetailPathByTaxonomicOrder = getSpeciesDetailPathByTaxonomicOrder(checklist);

    const speciesSet = setById.get('species-explorer');
    if (speciesSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            speciesSet.milestones,
            getSpeciesProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder)
        );
    }

    const habitatSet = setById.get('habitat-explorer');
    if (habitatSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            habitatSet.milestones,
            getLocationClassificationProgressEvents(sortedObservations, observationsByLocation, getAchievementHabitatLabel)
        );
    }

    const biomeSet = setById.get('biome-explorer');
    if (biomeSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            biomeSet.milestones,
            getLocationClassificationProgressEvents(sortedObservations, observationsByLocation, getAchievementBiomeLabel)
        );
    }

    const ecoregionSet = setById.get('ecoregion-explorer');
    if (ecoregionSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            ecoregionSet.milestones,
            getLocationClassificationProgressEvents(sortedObservations, observationsByLocation, getAchievementEcoregionLabel)
        );
    }

    const familySet = setById.get('family-finder');
    if (familySet) {
        assignMilestoneDatesFromProgress(milestoneDates, familySet.milestones, getFamilyProgressEvents(sortedObservations, checklist));
    }

    const genusSet = setById.get('genus-collector');
    if (genusSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            genusSet.milestones,
            getGenusProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder)
        );
    }

    const yearRoundSet = setById.get('year-round-regular');
    if (yearRoundSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            yearRoundSet.milestones,
            getYearRoundProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder)
        );
    }

    const liferRushSet = setById.get('lifer-rush');
    if (liferRushSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            liferRushSet.milestones,
            getLiferRushProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder)
        );
    }

    const taxonomicTourSet = setById.get('taxonomic-tour');
    if (taxonomicTourSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            taxonomicTourSet.milestones,
            getChecklistFamilyProgressEvents(sortedObservations, checklist)
        );
    }

    const familiarFacesSet = setById.get('familiar-faces');
    if (familiarFacesSet) {
        familiarFacesSet.milestones.forEach(milestone => {
            assignMilestoneDatesFromProgress(
                milestoneDates,
                [milestone],
                getFamiliarFacesProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder, milestone.threshold)
            );
        });
    }

    const countySet = setById.get('county-lister');
    if (countySet) {
        const seenCounties = new Set<string>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const countyKey = getCountyKey(observation);
            if (!countyKey || seenCounties.has(countyKey)) return [];

            seenCounties.add(countyKey);
            return [{
                value: seenCounties.size,
                date: observation.date,
                unlockContext: {
                    primaryLabel: observation.county || 'Unknown county',
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, countySet.milestones, progressEvents);
    }

    const stateProvinceSet = setById.get('state-province-lister');
    if (stateProvinceSet) {
        const seenStateProvinces = new Set<string>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const stateProvince = observation.stateProvince;
            if (!stateProvince || seenStateProvinces.has(stateProvince)) return [];

            seenStateProvinces.add(stateProvince);
            return [{
                value: seenStateProvinces.size,
                date: observation.date,
                unlockContext: {
                    primaryLabel: getStateProvinceLabel(stateProvince),
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, stateProvinceSet.milestones, progressEvents);
    }

    const countrySet = setById.get('world-traveler');
    if (countrySet) {
        assignMilestoneDatesFromProgress(milestoneDates, countrySet.milestones, getCountryProgressEvents(sortedObservations));
    }

    const bigYearSet = setById.get('big-year-chaser');
    if (bigYearSet) {
        const speciesByYear = new Map<string, Set<string>>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const year = getYear(observation.date);
            const speciesKey = getObservationSpeciesKey(observation);
            if (!year || !speciesKey || !isBigYearSpeciesObservation(observation, speciesDetailPathByTaxonomicOrder)) return [];

            const seenSpeciesForYear = speciesByYear.get(year) ?? new Set<string>();
            const previousSize = seenSpeciesForYear.size;
            seenSpeciesForYear.add(speciesKey);
            speciesByYear.set(year, seenSpeciesForYear);

            if (seenSpeciesForYear.size === previousSize) return [];

            const bestYearCount = Math.max(...Array.from(speciesByYear.values()).map(species => species.size));
            return [{
                value: bestYearCount,
                date: observation.date,
                unlockContext: {
                    primaryLabel: observation.commonName || 'Unknown species',
                    primaryDetailPath: getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder),
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, bigYearSet.milestones, progressEvents);
    }

    const bigDaySet = setById.get('big-day');
    if (bigDaySet) {
        const speciesByDate = new Map<string, Set<string>>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const date = observation.date;
            const speciesKey = getObservationSpeciesKey(observation);
            if (!date || !speciesKey || !isBigYearSpeciesObservation(observation, speciesDetailPathByTaxonomicOrder)) return [];

            const seenSpeciesForDate = speciesByDate.get(date) ?? new Set<string>();
            const previousSize = seenSpeciesForDate.size;
            seenSpeciesForDate.add(speciesKey);
            speciesByDate.set(date, seenSpeciesForDate);
            if (seenSpeciesForDate.size === previousSize) return [];

            return [{
                value: seenSpeciesForDate.size,
                date,
                unlockContext: {
                    primaryLabel: observation.commonName || 'Unknown species',
                    primaryDetailPath: getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder),
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, bigDaySet.milestones, progressEvents);
    }

    const bigChecklistSet = setById.get('big-checklist');
    if (bigChecklistSet) {
        const speciesByChecklist = new Map<string, Set<string>>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const checklistKey = getChecklistKey(observation);
            const speciesKey = getObservationSpeciesKey(observation);
            const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
            if (!checklistKey || !speciesKey || !speciesDetailPath || isExcludedBigYearTaxon(observation)) return [];

            const species = speciesByChecklist.get(checklistKey) ?? new Set<string>();
            const previousCount = species.size;
            species.add(speciesKey);
            speciesByChecklist.set(checklistKey, species);
            if (species.size === previousCount) return [];

            return [{
                value: species.size,
                date: observation.date,
                unlockContext: {
                    primaryLabel: observation.commonName || 'Unknown species',
                    primaryDetailPath: speciesDetailPath,
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, bigChecklistSet.milestones, progressEvents);
    }

    const birdingStreakSet = setById.get('birding-streak');
    if (birdingStreakSet) {
        const progressEvents = getBirdingStreakProgressEvents(sortedObservations).map(event => ({
            value: event.value,
            date: event.date,
            unlockContext: {
                primaryLabel: `${event.value.toLocaleString()}-day birding streak`,
                secondaryLabel: event.observation.location,
                secondaryDetailPath: event.observation.locationId
                    ? buildHotspotDetailPath(event.observation.locationId)
                    : undefined
            }
        }));

        assignMilestoneDatesFromProgress(milestoneDates, birdingStreakSet.milestones, progressEvents);
    }

    const weekendWarriorSet = setById.get('weekend-warrior');
    if (weekendWarriorSet) {
        const progressEvents = getWeekendWarriorProgressEvents(sortedObservations).map(event => ({
            value: event.value,
            date: event.date,
            unlockContext: {
                primaryLabel: `${event.value.toLocaleString()} weekends in ${event.year}`,
                secondaryLabel: event.observation.location,
                secondaryDetailPath: event.observation.locationId
                    ? buildHotspotDetailPath(event.observation.locationId)
                    : undefined
            }
        }));

        assignMilestoneDatesFromProgress(milestoneDates, weekendWarriorSet.milestones, progressEvents);
    }

    const photoSet = setById.get('photographer');
    if (photoSet) {
        const photographedSpecies = new Set<string>();
        const progressEvents = sortedObservations.flatMap(observation => {
            const speciesKey = getObservationSpeciesKey(observation);
            const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
            if (
                getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length <= 0 ||
                !speciesKey ||
                !speciesDetailPath ||
                photographedSpecies.has(speciesKey)
            ) return [];

            photographedSpecies.add(speciesKey);
            return [{
                value: photographedSpecies.size,
                date: observation.date,
                unlockContext: {
                    primaryLabel: observation.commonName || 'Unknown species',
                    primaryDetailPath: speciesDetailPath,
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        });

        assignMilestoneDatesFromProgress(milestoneDates, photoSet.milestones, progressEvents);
    }

    const photoCoverageSet = setById.get('photo-coverage');
    if (photoCoverageSet) {
        const lifeListSpecies = new Set(lifeList.map(getObservationSpeciesKey).filter(Boolean));
        const photographedSpecies = new Set<string>();
        const progressEvents = lifeListSpecies.size ? sortedObservations.flatMap(observation => {
            const speciesKey = getObservationSpeciesKey(observation);
            const speciesDetailPath = getSpeciesDetailPathForObservation(observation, speciesDetailPathByTaxonomicOrder);
            if (
                getMacaulayCatalogNumbers(observation.mlCatalogNumbers).length <= 0 ||
                !speciesKey ||
                !speciesDetailPath ||
                !lifeListSpecies.has(speciesKey) ||
                photographedSpecies.has(speciesKey)
            ) return [];

            photographedSpecies.add(speciesKey);
            return [{
                value: Math.round((photographedSpecies.size / lifeListSpecies.size) * 100),
                date: observation.date,
                unlockContext: {
                    primaryLabel: observation.commonName || 'Unknown species',
                    primaryDetailPath: speciesDetailPath,
                    secondaryLabel: observation.location,
                    secondaryDetailPath: observation.locationId ? buildHotspotDetailPath(observation.locationId) : undefined
                }
            }];
        }) : [];

        assignMilestoneDatesFromProgress(milestoneDates, photoCoverageSet.milestones, progressEvents);
    }

    const familyAlbumSet = setById.get('family-album');
    if (familyAlbumSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            familyAlbumSet.milestones,
            getPhotoFamilyProgressEvents(sortedObservations, checklist)
        );
    }

    const illustratedChecklistSet = setById.get('illustrated-checklist');
    if (illustratedChecklistSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            illustratedChecklistSet.milestones,
            getIllustratedChecklistProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder)
        );
    }

    const liferLensSet = setById.get('lifer-lens');
    if (liferLensSet) {
        assignMilestoneDatesFromProgress(
            milestoneDates,
            liferLensSet.milestones,
            getLiferLensProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder)
        );
    }

    return milestoneDates;
}

const buildMetricSnapshot = ({rawData, lifeList, observationsByLocation, checklist}: AchievementsViewProps): AchievementMetricSnapshot => {
    const latestObservation = [...rawData].sort((left, right) => getDateTime(right.date, right.time) - getDateTime(left.date, left.time))[0];
    const latestObservationYear = latestObservation?.date ? parseInt(getYear(latestObservation.date), 10) : undefined;
    const speciesDetailPathByTaxonomicOrder = getSpeciesDetailPathByTaxonomicOrder(checklist);

    return {
        species: getUniqueSpeciesCount(lifeList),
        habitats: getUniqueLocationClassificationCount(observationsByLocation, getAchievementHabitatLabel),
        biomes: getUniqueLocationClassificationCount(observationsByLocation, getAchievementBiomeLabel),
        ecoregions: getUniqueLocationClassificationCount(observationsByLocation, getAchievementEcoregionLabel),
        families: getSeenFamilyCount(checklist),
        counties: new Set(rawData.map(getCountyKey).filter(Boolean)).size,
        stateProvinces: new Set(rawData.map(observation => observation.stateProvince).filter(Boolean)).size,
        countries: new Set(rawData.map(getCountryCode).filter(Boolean)).size,
        bigYearSpecies: getBigYearSpeciesCount(rawData, speciesDetailPathByTaxonomicOrder),
        bigDaySpecies: getBigDaySpeciesCount(rawData, speciesDetailPathByTaxonomicOrder),
        bigChecklist: getBigChecklistSpeciesCount(rawData, speciesDetailPathByTaxonomicOrder),
        currentYearSpecies: getCurrentYearSpeciesCount(rawData, speciesDetailPathByTaxonomicOrder),
        birdingStreak: getLongestBirdingStreak(rawData),
        weekendWarrior: getWeekendWarriorCount(rawData),
        photoSpecies: getPhotoSpeciesCount(rawData, speciesDetailPathByTaxonomicOrder),
        photoCoverage: getPhotoCoverage(rawData, lifeList, speciesDetailPathByTaxonomicOrder),
        photoFamilies: getPhotoFamilyCount(rawData, checklist),
        illustratedChecklist: getIllustratedChecklistCount(rawData, speciesDetailPathByTaxonomicOrder),
        liferLens: getLiferLensCount(rawData, speciesDetailPathByTaxonomicOrder),
        genera: getGenusCount(rawData, speciesDetailPathByTaxonomicOrder),
        yearRoundSpecies: getYearRoundSpeciesCount(rawData, speciesDetailPathByTaxonomicOrder),
        liferRush: getLiferRushCount(rawData, speciesDetailPathByTaxonomicOrder),
        checklistFamilies: getChecklistFamilyCount(rawData, checklist),
        familiarRegulars10: getFamiliarRegularCount(rawData, speciesDetailPathByTaxonomicOrder, 10),
        familiarRegulars25: getFamiliarRegularCount(rawData, speciesDetailPathByTaxonomicOrder, 25),
        familiarRegulars50: getFamiliarRegularCount(rawData, speciesDetailPathByTaxonomicOrder, 50),
        familiarRegulars100: getFamiliarRegularCount(rawData, speciesDetailPathByTaxonomicOrder, 100),
        latestObservationDate: latestObservation?.date,
        latestObservationYear
    };
}

const getMetricValue = (metrics: AchievementMetricSnapshot, metricId: AchievementMetricId) => {
    return metrics[metricId];
}

const getMilestoneValue = (
    metrics: AchievementMetricSnapshot,
    setDefinition: AchievementSetDefinition,
    milestone: AchievementMilestoneDefinition
) => getMetricValue(metrics, milestone.metricId ?? setDefinition.metricId);

const getBigYearInProgressValue = (metrics: AchievementMetricSnapshot) => {
    return metrics.currentYearSpecies;
}

const getAchievementProgressPercent = (currentValue: number, threshold: number) => {
    const percent = Math.max(0, Math.min(100, Math.round((currentValue / threshold) * 100)));

    return currentValue > 0 && percent === 0 ? 1 : percent;
}

const buildAchievementDataPointCollection = (
    setId: string,
    props: AchievementsViewProps
): {dataPointTitle: string, dataPoints: AchievementDataPoint[]} | undefined => {
    const sortedObservations = getSortedObservations(props.rawData);
    const speciesDetailPathByTaxonomicOrder = getSpeciesDetailPathByTaxonomicOrder(props.checklist);
    const config = setId === 'habitat-explorer'
        ? {title: 'Habitats Counted', getLabel: getAchievementHabitatLabel}
        : setId === 'biome-explorer'
            ? {title: 'Biomes Counted', getLabel: getAchievementBiomeLabel}
            : setId === 'ecoregion-explorer'
                ? {title: 'Ecoregions Counted', getLabel: getAchievementEcoregionLabel}
                : undefined;
    if (setId === 'year-round-regular') {
        return {
            dataPointTitle: 'Year-Round Species',
            dataPoints: buildYearRoundDataPointCollection(sortedObservations, speciesDetailPathByTaxonomicOrder)
        };
    }
    const dataPointTitle = setId === 'family-finder'
        ? 'Families Counted'
        : setId === 'family-album'
            ? 'Families Photographed'
        : setId === 'world-traveler'
            ? 'Countries Counted'
            : setId === 'species-explorer'
                ? 'Species Counted'
            : setId === 'county-lister'
                    ? 'Counties Counted'
                    : setId === 'state-province-lister'
                        ? 'States/Provinces Counted'
                        : setId === 'photographer'
                            ? 'Species Photographed'
                            : setId === 'lifer-lens'
                                ? 'Photographed Lifers'
            : config?.title;
    const progressEvents = setId === 'family-finder'
        ? getFamilyProgressEvents(sortedObservations, props.checklist)
        : setId === 'family-album'
            ? getPhotoFamilyProgressEvents(sortedObservations, props.checklist)
        : setId === 'world-traveler'
            ? getCountryProgressEvents(sortedObservations)
            : setId === 'species-explorer'
                ? getSpeciesProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder)
            : setId === 'county-lister'
                    ? getCountyProgressEvents(sortedObservations)
                    : setId === 'state-province-lister'
                        ? getStateProvinceProgressEvents(sortedObservations)
                        : setId === 'photographer'
                            ? getPhotoSpeciesProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder)
                            : setId === 'lifer-lens'
                                ? getLiferLensProgressEvents(sortedObservations, speciesDetailPathByTaxonomicOrder)
            : config
                ? getLocationClassificationProgressEvents(sortedObservations, props.observationsByLocation, config.getLabel)
                : undefined;
    if (!dataPointTitle || !progressEvents) return undefined;

    const dataPoints = progressEvents.map(event => {
        const unlockContext: AchievementUnlockContext = event.unlockContext;

        return {
            label: unlockContext.primaryLabel,
            detailPath: unlockContext.primaryDetailPath,
            firstObservedDate: event.date,
            locationLabel: unlockContext.secondaryLabel,
            locationDetailPath: unlockContext.secondaryDetailPath
        };
    });

    return {
        dataPointTitle,
        dataPoints
    };
}

const getCompletedSetsCount = (sets: AchievementSetSummary[]) => {
    return sets.filter(set => set.earnedCount === set.totalCount).length;
}

const buildSetSummaries = (metrics: AchievementMetricSnapshot) => {
    return ACHIEVEMENT_SET_DEFINITIONS.map((setDefinition): AchievementSetSummary => {
        const currentValue = getMetricValue(metrics, setDefinition.metricId);
        const earnedCount = setDefinition.milestones.filter(milestone => (
            getMilestoneValue(metrics, setDefinition, milestone) >= milestone.threshold
        )).length;
        const totalCount = setDefinition.milestones.length;
        const nextMilestone = setDefinition.milestones.find(milestone => (
            getMilestoneValue(metrics, setDefinition, milestone) < milestone.threshold
        ));

        return {
            id: setDefinition.id,
            title: setDefinition.title,
            description: setDefinition.description,
            theme: setDefinition.theme,
            color: setDefinition.color,
            icon: setDefinition.icon,
            earnedCount,
            totalCount,
            percent: totalCount ? Math.round((earnedCount / totalCount) * 100) : 0,
            nextMilestone,
            currentValue
        };
    });
}

const buildEarnedAchievements = (metrics: AchievementMetricSnapshot, milestoneDates: Map<string, MilestoneUnlockDetail>): EarnedAchievement[] => {
    return ACHIEVEMENT_SET_DEFINITIONS.flatMap(setDefinition => {
        return setDefinition.milestones
            .filter(milestone => getMilestoneValue(metrics, setDefinition, milestone) >= milestone.threshold)
            .map(milestone => ({
                id: milestone.id,
                setId: setDefinition.id,
                setTitle: setDefinition.title,
                title: milestone.title,
                description: milestone.description,
                rarity: milestone.rarity,
                color: setDefinition.color,
                icon: setDefinition.icon,
                points: milestone.points,
                earnedDate: milestoneDates.get(milestone.id)?.date
            }));
    }).sort((left, right) => {
        const dateDifference = getDateTime(right.earnedDate) - getDateTime(left.earnedDate);
        if (dateDifference !== 0) return dateDifference;

        return right.points - left.points;
    });
}

const buildInProgressAchievements = (metrics: AchievementMetricSnapshot): InProgressAchievement[] => {
    return ACHIEVEMENT_SET_DEFINITIONS.map(setDefinition => {
        const earnedValue = getMetricValue(metrics, setDefinition.metricId);
        const currentValue = setDefinition.id === 'big-year-chaser'
            ? getBigYearInProgressValue(metrics)
            : earnedValue;

        if (setDefinition.id === 'big-year-chaser' && currentValue <= 0) {
            return undefined;
        }

        const nextMilestone = setDefinition.milestones.find(milestone => (
            setDefinition.id === 'big-year-chaser'
                ? currentValue
                : getMilestoneValue(metrics, setDefinition, milestone)
        ) < milestone.threshold);
        if (!nextMilestone) return undefined;

        return {
            id: nextMilestone.id,
            setId: setDefinition.id,
            setTitle: setDefinition.title,
            title: nextMilestone.title,
            description: nextMilestone.description,
            color: setDefinition.color,
            icon: setDefinition.icon,
            currentValue: setDefinition.id === 'big-year-chaser'
                ? currentValue
                : getMilestoneValue(metrics, setDefinition, nextMilestone),
            threshold: nextMilestone.threshold,
            percent: getAchievementProgressPercent(
                setDefinition.id === 'big-year-chaser'
                    ? currentValue
                    : getMilestoneValue(metrics, setDefinition, nextMilestone),
                nextMilestone.threshold
            )
        };
    }).filter((item): item is InProgressAchievement => Boolean(item))
        .sort((left, right) => right.percent - left.percent || right.currentValue - left.currentValue);
}

export const formatAchievementDate = (date?: string) => {
    if (!date) return 'Milestone unlocked';

    const parsed = DateTime.fromISO(date);
    return parsed.isValid ? parsed.toFormat('LLL d, yyyy') : date;
}

export const formatAchievementValue = (value: number) => {
    return value.toLocaleString();
}

export const getAchievementSetDefinitionById = (setId: string) => {
    return ACHIEVEMENT_SET_DEFINITION_BY_ID.get(setId);
}

export const buildAchievementSetPath = (setId: string) => `/discover/achievements/${encodeURIComponent(setId)}`;

const getAchievementDerivedData = (props: AchievementsViewProps): AchievementDerivedDataCache => {
    const enrichmentSignature = buildAchievementEnrichmentSignature(props.observationsByLocation);
    if (
        achievementDerivedDataCache &&
        achievementDerivedDataCache.rawData === props.rawData &&
        achievementDerivedDataCache.lifeList === props.lifeList &&
        achievementDerivedDataCache.observationsByLocation === props.observationsByLocation &&
        achievementDerivedDataCache.checklist === props.checklist &&
        achievementDerivedDataCache.enrichmentSignature === enrichmentSignature
    ) {
        return achievementDerivedDataCache;
    }

    const metrics = buildMetricSnapshot(props);
    const sets = buildSetSummaries(metrics);
    const milestoneDates = buildMilestoneUnlockDates(props);
    const earnedAchievements = buildEarnedAchievements(metrics, milestoneDates);
    const inProgress = buildInProgressAchievements(metrics);

    achievementDerivedDataCache = {
        rawData: props.rawData,
        lifeList: props.lifeList,
        observationsByLocation: props.observationsByLocation,
        checklist: props.checklist,
        enrichmentSignature,
        metrics,
        sets,
        milestoneDates,
        earnedAchievements,
        inProgress
    };

    return achievementDerivedDataCache;
}

export const buildAchievementSetDetail = (setId: string, props: AchievementsViewProps): AchievementSetDetail | undefined => {
    const setDefinition = ACHIEVEMENT_SET_DEFINITION_BY_ID.get(setId);
    if (!setDefinition) return undefined;

    const achievementData = getAchievementDerivedData(props);
    const setSummary = achievementData.sets.find(set => set.id === setId);
    if (!setSummary) return undefined;
    const dataPointCollection = buildAchievementDataPointCollection(setId, props);

    const bigYearInProgressValue = setDefinition.id === 'big-year-chaser'
        ? getBigYearInProgressValue(achievementData.metrics)
        : undefined;
    const milestones: AchievementMilestoneProgress[] = setDefinition.milestones.map(milestone => {
        const milestoneValue = getMilestoneValue(achievementData.metrics, setDefinition, milestone);
        const earned = milestoneValue >= milestone.threshold;
        const currentValue = !earned && bigYearInProgressValue !== undefined
            ? bigYearInProgressValue
            : milestoneValue;

        return {
            ...milestone,
            earned,
            earnedDate: earned ? achievementData.milestoneDates.get(milestone.id)?.date : undefined,
            unlockContext: earned ? achievementData.milestoneDates.get(milestone.id)?.unlockContext : undefined,
            currentValue,
            percent: getAchievementProgressPercent(currentValue, milestone.threshold)
        };
    });

    return {
        set: setSummary,
        milestones,
        totalPoints: setDefinition.milestones.reduce((total, milestone) => total + milestone.points, 0),
        ...dataPointCollection
    };
}

export const buildAchievementDashboard = (props: AchievementsViewProps): AchievementDashboard => {
    const achievementData = getAchievementDerivedData(props);
    const completedSets = getCompletedSetsCount(achievementData.sets);
    const totalAchievements = ACHIEVEMENT_SET_DEFINITIONS.reduce((total, setDefinition) => total + setDefinition.milestones.length, 0);

    return {
        stats: {
            totalPoints: achievementData.earnedAchievements.reduce((total, achievement) => total + achievement.points, 0),
            earnedAchievements: achievementData.earnedAchievements.length,
            totalAchievements,
            completedSets,
            completedSetsPercent: achievementData.sets.length ? Math.round((completedSets / achievementData.sets.length) * 100) : 0
        },
        themeOptions: ACHIEVEMENT_THEME_LABELS,
        sets: achievementData.sets,
        recentEarned: achievementData.earnedAchievements.slice(0, 6),
        inProgress: achievementData.inProgress.slice(0, 4)
    };
}
